import React, { ComponentProps, PropsWithChildren, useEffect } from "react";
import Animated, {
  Easing,
  interpolateColor,
  ReduceMotion,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { styled, Text, View, ViewProps, XStack, YStack } from "tamagui";

import { Accordion } from "../../ui/accordion";
import { Score } from "../../ui/score";
import { SwitchWithLabel } from "../../ui/switch";
import {
  ExpandableArea,
  ScaleCardContainerProvider,
  ScaleCardContext,
  useScaleCardContainerContext,
  useScaleCardContext,
} from "./scale-card";

interface ToogleProps {
  label: string | undefined;
}

const Toogle: React.FC<ToogleProps> = ({ label }) => {
  const { toggle, openCards: items } = useScaleCardContainerContext();
  const { id } = useScaleCardContext();
  const open = id ? items.includes(id) : false;

  return (
    <View
      data-id={"scale-toggle"}
      height={"$5"}
      justifyContent="center"
      alignItems="center"
      backgroundColor={"$gray-50"}
      padding={"$3.4"}
      borderRadius={"$8"}
    >
      <SwitchWithLabel
        isChecked={open}
        onCheckedChange={() => toggle(id)}
        label={label}
      />
    </View>
  );
};

const ScaleState = styled(Text, {
  name: "ScaleState",
  variants: {
    variant: {
      default: {
        fontFamily: "$subtitle",
        color: "$gray-primary-900",
      },
    },
    state: {
      success: {
        color: "$score-8",
      },
      warning: {
        color: "$warning-500",
      },
      neutral: {
        color: "$gray-600",
      },
      critical: {
        color: "$score-2-1",
      },
      danger: {
        color: "$error-500",
      },
    },
    font: {
      md: {
        fontWeight: "$4",
        fontSize: "$3",
        lineHeight: "$3",
      },
    },
  } as const,
  defaultVariants: {
    variant: "default",
    font: "md",
    state: "neutral",
  },
});

type ScoreState = ComponentProps<typeof Score>["variant"];

interface HeaderProps {
  score: number | string | undefined | null;
  title: string | null | undefined;
  state: ScoreState;
  stateText?: string;
  toogleLabel: string | undefined;
}

const Header: React.FC<HeaderProps> = ({
  toogleLabel,
  title,
  state,
  stateText,
  score,
}) => {
  return (
    <View flex={1} flexShrink={1} alignItems="flex-start" gap={"$1.5"}>
      <XStack
        flex={1}
        gap={"$2"}
        width={"100%"}
        alignItems="center"
        justifyContent="space-between"
      >
        <YStack flex={1} width={"100%"} gap={"$0.5"} alignItems="flex-start">
          <Text
            fontFamily={"$body"}
            fontWeight={"$6"}
            fontSize={"$5"}
            lineHeight={"$4"}
            color={"$gray-950"}
          >
            {title}
          </Text>
          <ScaleState state={state}>{stateText}</ScaleState>
        </YStack>
        {!!score && (
          <Score size="md" variant={state}>
            {score}
          </Score>
        )}
      </XStack>
      <Toogle label={toogleLabel} />
    </View>
  );
};

const ScaleCardContainer: React.FC<ViewProps> = ({ children, ...rest }) => {
  return (
    <View width={"100%"} flexDirection="column" gap={"$2"} {...rest}>
      {children}
    </View>
  );
};

type ScaleCardBodyProps = PropsWithChildren & {
  id: NonNullable<string>;
};

const ScaleCardBody: React.FC<ScaleCardBodyProps> = ({ children, id }) => {
  const height = useSharedValue(0);
  const { openCards: items } = useScaleCardContainerContext();

  const open = items.includes(id);

  const colorValue = useSharedValue(open ? 1 : 0);

  useEffect(() => {
    colorValue.value = withTiming(open ? 1 : 0, {
      duration: 300,
      easing: Easing.inOut(Easing.ease),
      reduceMotion: ReduceMotion.Never,
    });
  }, [open, colorValue]);

  const animate = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(
        colorValue.value,
        [0, 1],
        ["white", "#FBF9F9"],
      ),
      borderWidth: 1.5,
      borderColor: "#7D8291",
      borderRadius: 32,
      boxShadow: "0 3px 0 0 #D3D3D3",
      paddingVertical: 24,
      paddingHorizontal: 24,
      position: "relative",
    };
  });

  return (
    <ScaleCardContext.Provider value={{ id, height, danger: false }}>
      <Animated.View style={[animate]}>{children}</Animated.View>
    </ScaleCardContext.Provider>
  );
};

export const MobileScaleCard = {
  Provider: ScaleCardContainerProvider,
  Header,
  ExpandableArea,
  Container: ScaleCardContainer,
  Body: ScaleCardBody,
  Accordion,
};
